import {
  CustomMappingOption,
  CustomMappingType,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import { ServiceProviderHandler } from '@principle-theorem/principle-core';
import {
  Firestore,
  IBlobFilenamePair,
  WithRef,
  XSLXImporterExporter,
  asyncForEach,
  getEnumValues,
  snapshot,
} from '@principle-theorem/shared';
import { compact, sortBy, uniqBy } from 'lodash';
import { of } from 'rxjs';
import { BaseCustomMappingHandler } from '../base-custom-mapping-handler';
import { CustomMapping } from '../custom-mapping';
import { IBaseMigrationItemCode } from '../interfaces';
import { PracticeMigration } from '../practice-migrations';
import { BaseSourceEntity } from '../source/base-source-entity';
import { TranslationMapHandler } from '../translation-map';
import { ITEM_CODE_TO_NOTE_DEFAULT_MAPPINGS } from './ada-code-mappings';
import {
  ITEM_CODE_TO_NOTE_CUSTOM_MAPPING_TYPE,
  ItemCodeNoteType,
  ItemCodesToNotesXLSX,
} from './item-codes-to-notes-xlsx';
import { XLSXToItemCodesToNotes } from './xlsx-to-item-code-to-notes';

export const ITEM_CODE_TO_NOTE_MAPPING = CustomMapping.init({
  metadata: {
    label: 'Item Codes to Notes',
    description: `
      Use this to configure where the note content of Item Codes from the source PMS should be mapped to in Principle.
        - Sterilisation Record: This will become a sterilisation record in Principle. (Not done for Exact migrations).
    `,
    type: ITEM_CODE_TO_NOTE_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.SelectionList,
  labelOverrides: {
    sourceIdentifier: 'Id',
    sourceLabel: 'Item Code',
    destinationIdentifier: 'Note Type',
    associatedValue: '',
  },
});

export abstract class ItemCodeToNoteMappingHandler<
  ADAItem extends ISourceEntityTypes,
  SourceEntity extends BaseSourceEntity<ADAItem>,
> extends BaseCustomMappingHandler<object, ItemCodeNoteType> {
  customMapping = ITEM_CODE_TO_NOTE_MAPPING;
  abstract sourceEntity: SourceEntity;
  abstract translateFn: (item: ADAItem['data']) => IBaseMigrationItemCode;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const itemCodes = await this._getItemCodeOptions(migration);
    return itemCodes.map((record) => ({
      label: !record.description
        ? record.itemCode
        : `${record.itemCode} - ${record.description}`,
      value: record.itemCode,
    }));
  }

  async getSelectionListOptions(
    _migration: WithRef<IPracticeMigration>
  ): Promise<CustomMappingOption[]> {
    return snapshot(
      of(
        getEnumValues(ItemCodeNoteType).map((noteType) => ({
          value: noteType,
          description: noteType,
          hasAssociatedValue: false,
        }))
      )
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(fileName, itemCodes, translator);
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, itemCodes, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(fileName, itemCodes, translator);
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XLSXToItemCodesToNotes()
    );
    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const recordsForUpload = items.map((item) => {
      const label = item.description
        ? `${item.code} - ${item.description}`
        : item.code;
      const value = sourceOptions.find(
        (sourceOption) => sourceOption.label === label
      )?.value;
      if (!value) {
        return;
      }

      const mapTo = item.mapTo;
      if (mapTo && !getEnumValues(ItemCodeNoteType).includes(mapTo)) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find note type for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      return {
        destinationValue: { value: mapTo },
        sourceIdentifier: value,
        sourceLabel: label,
      };
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    itemCodes: IBaseMigrationItemCode[];
    translator: ItemCodesToNotesXLSX;
  }> {
    const fileName = this.getFileName();
    const itemCodes = await this._getItemCodeOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new ItemCodesToNotesXLSX(
      await this.getRecords(translationMap),
      ITEM_CODE_TO_NOTE_DEFAULT_MAPPINGS
    );
    return { fileName, itemCodes, translator };
  }

  private async _getItemCodeOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBaseMigrationItemCode[]> {
    const records = await this.sourceEntity
      .getAllRecords$(migration)
      .toPromise();
    const options = records
      .map((record) => record.data)
      .map(this.translateFn)
      .filter(
        (record) => !ServiceProviderHandler.findServiceCode(record.itemCode)
      );
    return sortBy(
      uniqBy(options, (option) => option.itemCode),
      'itemCode'
    );
  }
}
